﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../../utilities/crud-functions';
import { ModalMessageType } from '../../models/enums';
import { RequirementSpecification } from '../../models/Testing/requirement-specification';
import { RequirementCategory } from '../../models/Testing/requirement-category';
import { ItemBasicInformation } from '../../models/item-basic-information';
import ItemBasicInformationDialog from '../ItemBasicInformationDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import GlobalDialog from '../Modal/GlobalDialog';
import { ToastProps } from '../../models/toast-props';

import DataGrid, {
    Column,
    FilterRow,
    GroupPanel,
    Selection,
    HeaderFilter,
    Pager,
    Paging,
    Editing,
    Popup,
    Form,
    Lookup,
    Button as ColumnButton,
    SearchPanel,
    Toolbar,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import { Item } from 'devextreme-react/form';
import { Popup as DialogPopup } from 'devextreme-react/popup';


let key = 0;

export interface Props {
    projectId: string;
    category: RequirementCategory;
    isReadOnly: boolean;
    onClose?: () => void;
}

export default function RequirementSpecificationsEditor(props: Props) {
    const [subCategories, setSubcategories] =
        useState<RequirementCategory[]>();

    const [specifications, setSpecifications] =
        useState<RequirementSpecification[]>();

    const [selectedSpecification, setSelectedSpecification] =
        useState<RequirementSpecification>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    ++key;

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="save-toolbar pw-margin-right-05">
                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height={'1.8rem'}
                        onClick={onSaveClick} />

                    <Button id="cancelButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height={'1.8rem'}
                        onClick={onCloseClick} />
                </div>
            </div>

            <div className="pw-margin-bottom-05">
                <DataGrid
                    height="80vh" width="100%"
                    keyExpr="requirementSpecificationId"
                    dataSource={specifications ? specifications : []}
                    onSelectionChanged={onSpecificationsSelectionChanged}
                    onRowValidating={onSpecificationsRowValidating}
                    onRowRemoving={onSpecificationsRowRemoving}
                    allowColumnResizing={true}
                    wordWrapEnabled={true}
                    columnResizingMode='widget'
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Selection mode={'single'} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <GroupPanel visible={false} />
                    <SearchPanel visible={true} />

                    <Column type="buttons" width="7rem">
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showSpecificationsAuditDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onSpecificationInformationButtonClicked} />
                    </Column>

                    <Column dataField='requirementSpecificationNumber' caption="Spec#" width='8rem' dataType="string" />

                    <Column dataField='subCategoryId' caption="Subcategory" width="8rem" dataType="string">
                        <Lookup dataSource={subCategories ? subCategories : []}
                            displayExpr='name'
                            valueExpr='requirementCategoryId' />
                    </Column>

                    <Column dataField='description' dataType="string" width="50%" />
                    <Column dataField='solution' encodeHtml={false} width='50%' dataType="string" />

                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">{`${props.category && props.category.name} Specifications`}</div>
                            </div>
                        </ToolBarItem>
                        <ToolBarItem location="after" name="searchPanel" />
                        <ToolBarItem location="after" name="addRowButton" />
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={true}
                        allowDeleting={true} >

                        <Popup title="Specification Entry" showTitle={true} fullScreen={true} />

                        <Form colCount={2}>
                            <Item dataField="requirementSpecificationNumber" />
                            <Item dataField="subCategoryId" />

                            <Item dataField="description" colSpan={2} colCount={2}
                                editorType="dxTextArea" editorOptions={{ height: "10rem" }} />

                            <Item dataField="solution" colSpan={2} colCount={2}
                                editorType="dxTextArea" editorOptions={{ height: "10rem" }} />
                        </Form>

                    </Editing>
                </DataGrid>
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function onSaveClick() {

    }

    function onCloseClick() {
        if (props.onClose)
            props.onClose();
    }

    function onSpecificationsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedSpecification(e.selectedRowsData[0]);
    }

    async function onSpecificationsRowValidating(e: any) {
        console.log("onSpecificationsRowValidating");
        console.log(e);

        if (e.oldData) {
            let specification = e.oldData as RequirementSpecification;

            if (specification) {
                let updatedSpecification = { ...specification, ...e.newData };

                updateSpecification(updatedSpecification)
                    .then(() => {

                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Specification");
                    });
            }
        }
        else {
            if (e.newData) {
                let specification = e.newData as RequirementSpecification;
                specification.requirementProjectId = props.projectId;

                await insertSpecification(specification)
                    .then(() => {

                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Specification");
                    });
            }
        }
    }


    function onSpecificationsRowRemoving(e: any) {

    }

    async function getSpecifications() {
        await CrudFunctions.getData(`testing/specifications/${props.projectId}/${props.category.requirementCategoryId}`)
            .then(data => {
                setSpecifications(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Specifications");
            });
    }

    async function getSubcategories() {
        await CrudFunctions.getData(`testing/subcategories/${props.category.requirementCategoryId}`)
            .then(data => {
                setSubcategories(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Categories");
            });
    }

    //POST functions

    async function insertSpecification(specification: RequirementSpecification) {
        return CrudFunctions.postData("testing/requirementspecification", specification);
    }

    //PUT functions

    async function updateSpecification(specification: RequirementSpecification) {
        return CrudFunctions.putData("testing/requirementspecification", specification);
    }

    // DELETE functions


    async function getAllData() {
        if (props.projectId && props.category && props.category.requirementCategoryId) {

            await Promise.all([
                getSpecifications(),
                getSubcategories()
            ])
        }
    }

    function showSpecificationsAuditDialog(e: any) {
        if (e.row && e.row.data) {
            //todo
        }
    }

    function onSpecificationInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.requirementCategoryId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            showItemBasicInformationDialog();
        }
    }

    function showItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(true);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    //other

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}





